function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import useField from "../hooks/internal/useField";
import { mergeProps } from "../utils/component";
import Field from "./internal/Field";
import Grid from "./Grid";
import InternalInput from "./internal/InternalInput";
import InternalRadioGroup from "./internal/InternalRadioGroup";
import InternalSelect from "./internal/InternalSelect";
import { jsx as ___EmotionJSX } from "@emotion/core";
var ALL_FREQUENCY_OPTIONS = [{
  label: "Annually",
  value: "annually"
}, {
  label: "Quarterly",
  value: "quarterly"
}, {
  label: "Monthly",
  value: "monthly"
}, {
  label: "Fortnightly",
  value: "fortnightly"
}, {
  label: "Weekly",
  value: "weekly"
}];
var COLORS = InternalInput.COLORS,
    NUMERIC_REGEX = InternalInput.NUMERIC_REGEX;
var MODES = ["radio-group", "select"];

function isFrequencySelected(frequency, frequencyPropsMap) {
  return ALL_FREQUENCY_OPTIONS.findIndex(function (option) {
    return option.value === frequency;
  }) > -1 && frequencyPropsMap[frequency] === true;
}

var DEFAULT_PROPS = {
  color: InternalInput.DEFAULT_PROPS.color,
  mode: "radio-group",
  annually: true,
  quarterly: true,
  monthly: true,
  fortnightly: true,
  weekly: true,
  disabled: false,
  selectPlaceholder: InternalSelect.DEFAULT_PROPS.placeholder,
  optional: false,
  validate: function validate(value, _ref) {
    var isInputEmpty = _ref.isInputEmpty,
        isFrequencyEmpty = _ref.isFrequencyEmpty;
    var errors = [];

    if (isInputEmpty(value.amount)) {
      errors.push("Please enter an amount.");
    }

    if (NUMERIC_REGEX.test(value.amount) === false) {
      errors.push("Amount can contain only digits.");
    }

    if (isFrequencyEmpty(value.frequency)) {
      errors.push("Please select a frequency.");
    }

    return errors;
  }
};
Frequency.ALL_FREQUENCY_OPTIONS = ALL_FREQUENCY_OPTIONS;
Frequency.COLORS = COLORS;
Frequency.MODES = MODES;
Frequency.DEFAULT_PROPS = DEFAULT_PROPS;

function Frequency(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    mode: function mode(_mode) {
      return MODES.includes(_mode);
    },
    annually: function annually(_annually) {
      return typeof _annually === "boolean";
    },
    quarterly: function quarterly(_quarterly) {
      return typeof _quarterly === "boolean";
    },
    monthly: function monthly(_monthly) {
      return typeof _monthly === "boolean";
    },
    fortnightly: function fortnightly(_fortnightly) {
      return typeof _fortnightly === "boolean";
    },
    weekly: function weekly(_weekly) {
      return typeof _weekly === "boolean";
    },
    amountPrefix: function amountPrefix(_amountPrefix) {
      return typeof _amountPrefix === "string" && _amountPrefix.length > 0;
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    optional: function optional(_optional) {
      return typeof _optional === "boolean";
    }
  });
  var name = mergedProps.name,
      mode = mergedProps.mode,
      label = mergedProps.label,
      annually = mergedProps.annually,
      quarterly = mergedProps.quarterly,
      monthly = mergedProps.monthly,
      fortnightly = mergedProps.fortnightly,
      weekly = mergedProps.weekly,
      amountPrefix = mergedProps.amountPrefix,
      amountPlaceholder = mergedProps.amountPlaceholder,
      selectPlaceholder = mergedProps.selectPlaceholder,
      amountMaxLength = mergedProps.amountMaxLength,
      helpText = mergedProps.helpText,
      disabled = mergedProps.disabled,
      optional = mergedProps.optional,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      testId = mergedProps.testId;

  var _useState = useState(function () {
    return "frequency-label-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      labelId = _useState2[0];

  var _useState3 = useState(function () {
    return "frequency-aux-".concat(nanoid());
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      auxId = _useState4[0];

  var frequencyPropsMap = useMemo(function () {
    return {
      annually: annually,
      quarterly: quarterly,
      monthly: monthly,
      fortnightly: fortnightly,
      weekly: weekly
    };
  }, [annually, quarterly, monthly, fortnightly, weekly]);
  var frequencyOptions = useMemo(function () {
    return ALL_FREQUENCY_OPTIONS.reduce(function (acc, option) {
      if (frequencyPropsMap[option.value] === true) {
        acc.push(option);
      }

      return acc;
    }, []);
  }, [frequencyPropsMap]);
  var isInputEmpty = useCallback(function (amount) {
    return amount.trim() === "";
  }, []);
  var isFrequencyEmpty = useCallback(function (frequency) {
    return isFrequencySelected(frequency, frequencyPropsMap) === false;
  }, [frequencyPropsMap]);
  var isEmpty = useCallback(function (_ref2) {
    var amount = _ref2.amount,
        frequency = _ref2.frequency;
    return isInputEmpty(amount) && isFrequencyEmpty(frequency);
  }, [isInputEmpty, isFrequencyEmpty]);
  var data = useMemo(function () {
    return _objectSpread({
      isInputEmpty: isInputEmpty,
      isFrequencyEmpty: isFrequencyEmpty,
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isInputEmpty, isFrequencyEmpty, isEmpty, validateData]);

  var _useField = useField("Frequency", {
    name: name,
    disabled: disabled,
    optional: optional,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      onChange = _useField.onChange,
      onMouseDown = _useField.onMouseDown;

  var inputComponent = ___EmotionJSX(InternalInput, {
    name: "".concat(name, ".amount"),
    parentName: name,
    variant: "numeric",
    prefix: amountPrefix,
    color: props.color,
    placeholder: amountPlaceholder,
    maxLength: amountMaxLength,
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value.amount,
    onChange: onChange
  });

  return ___EmotionJSX(Field, {
    optional: optional,
    disabled: disabled,
    label: label,
    labelId: labelId,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX("div", {
    "aria-invalid": hasErrors ? "true" : null,
    "aria-labelledby": labelId,
    "aria-describedby": helpText || hasErrors ? auxId : null
  }, mode === "radio-group" && ___EmotionJSX(Grid, {
    cols: 1,
    rowsGap: 1
  }, inputComponent, frequencyOptions.length > 0 && ___EmotionJSX(InternalRadioGroup, {
    name: "".concat(name, ".frequency"),
    parentName: name,
    color: props.color,
    options: frequencyOptions,
    columns: 2,
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    onMouseDown: onMouseDown,
    value: value.frequency,
    onChange: onChange
  })), mode === "select" && ___EmotionJSX(Grid, {
    cols: 2,
    colsGap: 1
  }, ___EmotionJSX(Grid.Item, {
    colSpan: "0"
  }, inputComponent), ___EmotionJSX(Grid.Item, {
    colSpan: "1"
  }, ___EmotionJSX(InternalSelect, {
    name: "".concat(name, ".frequency"),
    parentName: name,
    color: props.color,
    optional: optional,
    placeholder: selectPlaceholder,
    options: frequencyOptions,
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value.frequency,
    onChange: onChange
  })))));
}

Frequency.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.oneOf(COLORS),
  mode: PropTypes.oneOf(MODES),
  label: PropTypes.string.isRequired,
  annually: PropTypes.bool,
  quarterly: PropTypes.bool,
  monthly: PropTypes.bool,
  fortnightly: PropTypes.bool,
  weekly: PropTypes.bool,
  amountPrefix: PropTypes.string,
  amountPlaceholder: PropTypes.string,
  amountMaxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectPlaceholder: PropTypes.string,
  helpText: PropTypes.node,
  disabled: PropTypes.bool,
  optional: PropTypes.bool,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  testId: PropTypes.string
};
export default Frequency;